// v2/pages/admin/AdminLoginsV2.js — Yonetim / Giris Kayitlari (/v2/admin/logins)
// URL: ?q= &code= &user= &ip= &from= &to= &page= &per=
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  LogIn, Search, X, SlidersHorizontal, RotateCw, ChevronLeft, ChevronRight,
  Calendar, CalendarDays, Users, Store, Globe, AlertTriangle, Inbox, Clock,
  FileCheck2, History,
} from 'lucide-react';
import { useAdminLogins } from '../../hooks/useAdminUsers';
import useMedia from '../../hooks/useMedia';
import { formatNumber } from '../../utils/format';
import { zamanTR, gunKisa, gunUzun, basHarfler } from '../../utils/adminFormat';
import { SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminLoginsV2.module.css';
import '../../styles/tokens.css';

const VARSAYILAN = { page: '1', per: '25' };
const HIZLI_TARIH = [
  { id: 'today', label: 'Bugün' },
  { id: 'yesterday', label: 'Dün' },
  { id: '7', label: 'Son 7 gün' },
  { id: '30', label: 'Son 30 gün' },
];

const ymdKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const tarihTR = (ymd) => {
  if (!ymd) return '';
  const [y, a, g] = String(ymd).split('-');
  return y ? `${g}.${a}.${y}` : '';
};
const gunuAl = (iso) => (iso ? String(iso).slice(0, 10) : '');
const saatTR = (iso) => (iso ? String(iso).slice(11, 16) : '-');

function araligiCoz(id) {
  const bugun = new Date();
  if (id === 'today') return { from: ymdKey(bugun), to: ymdKey(bugun) };
  if (id === 'yesterday') {
    const d = new Date(bugun);
    d.setDate(d.getDate() - 1);
    return { from: ymdKey(d), to: ymdKey(d) };
  }
  const b = new Date(bugun);
  b.setDate(b.getDate() - (Number(id) - 1));
  return { from: ymdKey(b), to: ymdKey(bugun) };
}

function gunBasligi(g) {
  const bugun = new Date();
  const dun = new Date();
  dun.setDate(dun.getDate() - 1);
  if (g === ymdKey(bugun)) return 'Bugün';
  if (g === ymdKey(dun)) return 'Dün';
  return gunUzun(g) || tarihTR(g);
}

function GrafikIpucu({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  return (
    <div className={s.tip}>
      <div className={s.tipTitle}>{gunUzun(label)}</div>
      <div className={s.tipRow}><span>Giriş</span><b>{formatNumber(p.count)}</b></div>
      <div className={s.tipRow}><span>Kullanıcı</span><b>{formatNumber(p.users)}</b></div>
    </div>
  );
}

export default function AdminLoginsV2() {
  const [sp, setSp] = useSearchParams();
  const oku = useCallback((k) => sp.get(k) || VARSAYILAN[k] || '', [sp]);
  const mobil = useMedia('(max-width: 640px)');

  const [arama, setArama] = useState(sp.get('q') || '');
  const [filtreAcik, setFiltreAcik] = useState(false);

  useEffect(() => { setArama(sp.get('q') || ''); }, [sp]);

  const params = useMemo(() => ({
    q: sp.get('q') || '', code: sp.get('code') || '', user: sp.get('user') || '',
    ip: sp.get('ip') || '', from: sp.get('from') || '', to: sp.get('to') || '',
    page: oku('page'), per: oku('per'),
  }), [sp, oku]);

  const { data, loading, error, yenile } = useAdminLogins(params);

  const guncelle = useCallback((yeni, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yeni).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === VARSAYILAN[k]) p.delete(k);
        else p.set(k, v);
      });
      if (sayfaSifirla) p.delete('page');
      return p;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    const suAn = sp.get('q') || '';
    if (arama === suAn) return undefined;
    const t = setTimeout(() => guncelle({ q: arama.trim() }), 400);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const ilk = loading && !data;
  const ogeler = (data && data.items) || [];
  const o = (data && data.summary) || {};
  const seri = (data && data.series) || [];
  const sayfa = (data && data.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const toplam = (data && data.total) || 0;
  const per = Number(params.per) || 25;
  const ilkSira = (sayfa - 1) * per;
  const acikFiltre = ['code', 'user', 'ip', 'from', 'to'].filter((k) => params[k]).length;

  const gunOzet = useMemo(() => {
    const m = {};
    ogeler.forEach((x) => {
      const g = gunuAl(x.time);
      m[g] = (m[g] || 0) + 1;
    });
    return m;
  }, [ogeler]);

  const chipler = [];
  if (params.code) chipler.push({ id: 'code', ikon: Store, label: 'Cari', value: params.code, clear: { code: '' } });
  if (params.user) chipler.push({ id: 'user', ikon: Users, label: 'Kullanıcı', value: `#${params.user}`, clear: { user: '' } });
  if (params.ip) chipler.push({ id: 'ip', ikon: Globe, label: 'IP', value: params.ip, clear: { ip: '' } });
  if (params.from || params.to) {
    chipler.push({ id: 'date', ikon: CalendarDays, label: 'Tarih',
      value: `${tarihTR(params.from) || '…'} – ${tarihTR(params.to) || 'bugün'}`,
      clear: { from: '', to: '' } });
  }

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Giriş Kayıtları</h2>
          <p className={s.subtitle}>
            {ilk ? 'Yükleniyor…'
              : <><b>{formatNumber(toplam)}</b> kayıt · <b>{formatNumber(o.users)}</b> kullanıcı · {formatNumber(o.customers)} cari</>}
          </p>
        </div>
        <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
          <RotateCw size={15} className={loading ? s.spin : ''} />
          <span className={s.btnText}>Yenile</span>
        </button>
      </div>

      <div className={s.stats}>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tBlue}`}><LogIn size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.logins)}</span>
            <span className={s.statLabel}>Toplam giriş</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tGreen}`}><Users size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.users)}</span>
            <span className={s.statLabel}>Farklı kullanıcı</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tViolet}`}><Globe size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.ips)}</span>
            <span className={s.statLabel}>Farklı IP adresi</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${o.no_kvkk ? s.tAmber : s.tSlate}`}><FileCheck2 size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(o.no_kvkk)}</span>
            <span className={s.statLabel}>KVKK onayı yok</span>
          </span>
        </div>
      </div>

      {seri.length > 0 && (
        <section className={s.chartPanel}>
          <div className={s.chartHead}>
            <h3 className={s.chartTitle}><History size={16} /> Son 14 Gün</h3>
          </div>
          <div className={s.chartBody}>
            <ResponsiveContainer width="100%" height={mobil ? 150 : 180}>
              <BarChart data={seri} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" />
                <XAxis dataKey="date" tickFormatter={gunKisa} tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={false} tickLine={false} interval={mobil ? 3 : 1} minTickGap={6} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={false} tickLine={false} width={mobil ? 30 : 40} />
                <Tooltip content={<GrafikIpucu />} cursor={{ fill: 'rgba(37,99,199,.06)' }} />
                <Bar dataKey="count" fill="#2563C7" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <div className={s.toolbar}>
        <div className={s.searchBox}>
          <Search size={16} className={s.searchIcon} />
          <input type="search" className={s.search} placeholder="Ad, e-posta, cari kodu veya ünvanı…"
            value={arama} onChange={(e) => setArama(e.target.value)} aria-label="Giriş ara" />
          {arama && (
            <button type="button" className={s.clearBtn} onClick={() => setArama('')} aria-label="Temizle">
              <X size={15} />
            </button>
          )}
        </div>
        <button type="button" className={`${s.ghostBtn} ${filtreAcik ? s.ghostOn : ''}`}
          onClick={() => setFiltreAcik((v) => !v)} aria-expanded={filtreAcik}>
          <SlidersHorizontal size={15} />
          <span className={s.btnText}>Filtreler</span>
          {acikFiltre > 0 && <span className={s.badge}>{acikFiltre}</span>}
        </button>
      </div>

      {filtreAcik && (
        <div className={s.filters}>
          <div className={s.presets}>
            <span className={s.fieldLabel}><CalendarDays size={12} /> Hızlı tarih</span>
            <div className={s.presetBtns}>
              {HIZLI_TARIH.map((h) => {
                const a = araligiCoz(h.id);
                const secili = params.from === a.from && params.to === a.to;
                return (
                  <button key={h.id} type="button"
                    className={`${s.preset} ${secili ? s.presetOn : ''}`}
                    onClick={() => guncelle(secili ? { from: '', to: '' } : a)}>{h.label}</button>
                );
              })}
            </div>
          </div>
          <label className={s.field}>
            <span className={s.fieldLabel}><Calendar size={12} /> Başlangıç</span>
            <input type="date" className={s.input} value={params.from}
              onChange={(e) => guncelle({ from: e.target.value })} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}><Calendar size={12} /> Bitiş</span>
            <input type="date" className={s.input} value={params.to}
              onChange={(e) => guncelle({ to: e.target.value })} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}><Globe size={12} /> IP adresi</span>
            <input className={s.input} value={params.ip} placeholder="88.235…"
              onChange={(e) => guncelle({ ip: e.target.value.trim() })} />
          </label>
          {(acikFiltre > 0 || params.q) && (
            <button type="button" className={s.resetBtn} onClick={() => setSp({}, { replace: true })}>
              <X size={14} /> Temizle
            </button>
          )}
        </div>
      )}

      {chipler.length > 0 && (
        <div className={s.chipRow}>
          {chipler.map((c) => {
            const Ikon = c.ikon;
            return (
              <span key={c.id} className={s.filterChip}>
                <Ikon size={13} /> {c.label}: <b>{c.value}</b>
                <button type="button" onClick={() => guncelle(c.clear)} aria-label="Kaldır"><X size={13} /></button>
              </span>
            );
          })}
        </div>
      )}

      {error && (
        <div className={s.alert}>
          <AlertTriangle size={18} /> <span>{error}</span>
          <button type="button" className={s.alertBtn} onClick={yenile}>Tekrar dene</button>
        </div>
      )}

      <section className={s.panel}>
        {loading && !ilk && <span className={s.loadBar} aria-hidden="true" />}
        {ilk ? (
          <div className={s.panelBody}><SkelPanel rows={8} head={false} /></div>
        ) : ogeler.length === 0 ? (
          <div className={s.empty}>
            <Inbox size={24} />
            <p className={s.emptyTitle}>Giriş kaydı bulunamadı</p>
            <p className={s.emptyText}>Arama veya filtreleri değiştirmeyi deneyin.</p>
          </div>
        ) : (
          <>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={`${s.th} ${s.thNo}`}>#</th>
                    <th className={s.th}>Saat</th>
                    <th className={s.th}>Kullanıcı</th>
                    <th className={s.th}>Müşteri</th>
                    <th className={s.th}>IP Adresi</th>
                    <th className={s.th}>KVKK</th>
                  </tr>
                </thead>
                <tbody>
                  {ogeler.map((x, i) => {
                    const gun = gunuAl(x.time);
                    const yeniGun = gun && gun !== gunuAl(i > 0 ? ogeler[i - 1].time : '');
                    return (
                      <React.Fragment key={x.id}>
                        {yeniGun && (
                          <tr className={s.dayRow}>
                            <td className={s.dayTd} colSpan={6}>
                              <span className={s.dayLabel}>{gunBasligi(gun)}</span>
                              <span className={s.dayCount}>
                                {formatNumber(gunOzet[gun])} kayıt (bu sayfada)
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td className={`${s.td} ${s.tdNo}`}>{ilkSira + i + 1}</td>
                          <td className={`${s.td} ${s.time}`}>{saatTR(x.time)}</td>
                          <td className={s.td}>
                            <span className={s.userCell}>
                              <span className={s.avatar}>{basHarfler(x.name)}</span>
                              <span className={s.userText}>
                                <button type="button" className={s.userName}
                                  onClick={() => guncelle({ user: String(x.user_id) })}
                                  title="Bu kullanıcının girişlerini göster">
                                  {(x.name || '').trim() || '-'}
                                </button>
                                <span className={s.userMail} title={x.email || ''}>{x.email || '-'}</span>
                              </span>
                            </span>
                          </td>
                          <td className={s.td}>
                            {x.code ? (
                              <span className={s.cell}>
                                <Link to={`/v2/admin/customers/${encodeURIComponent(x.code)}`}
                                  className={s.custLink} title={x.customer_name || ''}>
                                  {x.customer_name || x.code}
                                </Link>
                                <button type="button" className={s.codeBtn}
                                  onClick={() => guncelle({ code: x.code })}
                                  title="Bu carinin girişlerini göster">{x.code}</button>
                              </span>
                            ) : <span className={s.dim}>-</span>}
                          </td>
                          <td className={s.td}>
                            <button type="button" className={s.ipBtn}
                              onClick={() => guncelle({ ip: x.ip || '' })}
                              title="Bu IP'nin girişlerini göster">{x.ip || '-'}</button>
                          </td>
                          <td className={s.td}>
                            <span className={`${s.pill} ${x.kvkk ? s.pillGreen : s.pillAmber}`}>
                              {x.kvkk ? 'Onaylı' : 'Onaysız'}
                            </span>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className={s.mList}>
              {ogeler.map((x, i) => {
                const gun = gunuAl(x.time);
                const yeniGun = gun && gun !== gunuAl(i > 0 ? ogeler[i - 1].time : '');
                return (
                  <li key={`m-${x.id}`} className={s.mItem}>
                    {yeniGun && (
                      <div className={s.mDay}>
                        <span className={s.dayLabel}>{gunBasligi(gun)}</span>
                        <span className={s.dayCount}>{formatNumber(gunOzet[gun])} kayıt</span>
                      </div>
                    )}
                    <div className={s.mRow}>
                      <span className={s.avatar}>{basHarfler(x.name)}</span>
                      <span className={s.userText}>
                        <span className={s.mName}>{(x.name || '').trim() || '-'}</span>
                        <span className={s.userMail}>{x.customer_name || x.code || '-'}</span>
                      </span>
                      <span className={s.mTime}>{saatTR(x.time)}</span>
                    </div>
                    <div className={s.mMeta}>
                      {x.code && <span>{x.code}</span>}
                      <span>{x.ip || '-'}</span>
                      {!x.kvkk && <span className={`${s.pill} ${s.pillAmber}`}>KVKK yok</span>}
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {formatNumber(ilkSira + 1)}–{formatNumber(ilkSira + ogeler.length)} / {formatNumber(toplam)}
              </span>
              <label className={s.perBox}>
                <span className={s.fieldLabel}>Sayfa başına</span>
                <select className={s.selectSm} value={params.per}
                  onChange={(e) => guncelle({ per: e.target.value })}>
                  {((data && data.page_sizes) || [25, 50, 100]).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className={s.pagerBtns}>
                <button type="button" className={s.pageBtn} disabled={sayfa <= 1 || loading}
                  onClick={() => guncelle({ page: String(sayfa - 1) }, false)} aria-label="Önceki">
                  <ChevronLeft size={16} />
                </button>
                <span className={s.pageNo}>{formatNumber(sayfa)} / {formatNumber(sonSayfa)}</span>
                <button type="button" className={s.pageBtn} disabled={sayfa >= sonSayfa || loading}
                  onClick={() => guncelle({ page: String(sayfa + 1) }, false)} aria-label="Sonraki">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <p className={s.note}>
        <Clock size={14} />
        Kayıtlar KVKK sözleşme onayıyla birlikte tutulur. Saatler Türkiye saatine göredir.
      </p>
    </div>
  );
}
